import { useEffect, useRef, useCallback, useState } from 'react';
import { useStore } from '../store';
import type { Note } from '../types';

// ─── useAutoSave ──────────────────────────────────────────────────────────────
export function useAutoSave(noteId: string | null, content: { title: string; body: string; plainText: string }) {
  const updateNote = useStore(s => s.updateNote);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!noteId) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { updateNote(noteId, content); }, 800);
    return () => clearTimeout(timer.current);
  }, [noteId, content.title, content.body]); // eslint-disable-line
}

// ─── useSearch ───────────────────────────────────────────────────────────────
export function useSearch() {
  const search      = useStore(s => s.search);
  const clearSearch = useStore(s => s.clearSearch);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleSearch = useCallback((query: string, folderId?: string | null, tag?: string | null) => {
    clearTimeout(timer.current);
    if (!query.trim()) { clearSearch(); return; }
    timer.current = setTimeout(() => search(query, folderId, tag), 300);
  }, [search, clearSearch]);
  return { handleSearch, clearSearch };
}

// ─── useAuth ─────────────────────────────────────────────────────────────────
export function useAuth() {
  const isAuthenticated = useStore(s => s.isAuthenticated);
  const authLoading     = useStore(s => s.authLoading);
  const user            = useStore(s => s.user);
  return { user, isAuthenticated, authLoading };
}

// ─── useNoteEditor ───────────────────────────────────────────────────────────
export function useNoteEditor(note: Note | null) {
  const [title, setTitle]     = useState(note?.title || '');
  const [body, setBody]       = useState(note?.body || '');
  const [plainText, setPlain] = useState(note?.plainText || '');
  useEffect(() => {
    setTitle(note?.title || '');
    setBody(note?.body || '');
    setPlain(note?.plainText || '');
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoSave(note?.id || null, { title, body, plainText });
  return { title, setTitle, body, setBody, plainText, setPlain };
}

// ─── useClickOutside ─────────────────────────────────────────────────────────
export function useClickOutside<T extends HTMLElement>(handler: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handler();
    };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [handler]);
  return ref;
}

// ─── useKeyboard ─────────────────────────────────────────────────────────────
export function useKeyboard() {
  const createNote  = useStore(s => s.createNote);
  const setOpenNote = useStore(s => s.setOpenNote);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'n') { e.preventDefault(); createNote(); }
      if (e.key === 'Escape')   { setOpenNote(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [createNote, setOpenNote]);
}

// ─── useRelativeTime ─────────────────────────────────────────────────────────
export function useRelativeTime(dateStr: string): string {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const calc = () => {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1)   return 'Just now';
      if (mins < 60)  return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)   return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days === 1) return 'Yesterday';
      if (days < 7)   return `${days} days ago`;
      return new Date(dateStr).toLocaleDateString('en-GB', { day:'numeric', month:'short' });
    };
    setLabel(calc());
    const t = setInterval(() => setLabel(calc()), 60000);
    return () => clearInterval(t);
  }, [dateStr]);
  return label;
}
